import { useMemo, useState } from "react";
import { PageHeader, useResource, friendlyTime } from "@/admin/shared";
import { Input, Select, StatusPill } from "@/admin/ui";
import DataTable from "@/admin/DataTable";

export default function AuditLogs() {
    const [user, setUser] = useState("");
    const [module, setModule] = useState("");
    const [action, setAction] = useState("");

    const query = useMemo(() => {
        const parts = [];
        if (user) parts.push(`user=${encodeURIComponent(user)}`);
        if (module) parts.push(`module=${encodeURIComponent(module)}`);
        if (action) parts.push(`action=${encodeURIComponent(action)}`);
        return parts.length ? `?${parts.join("&")}` : "";
    }, [user, module, action]);

    const { data, loading } = useResource(`/audit-logs${query}`, [query]);

    const cols = [
        {
            key: "action",
            label: "Action",
            render: (r) => (
                <div>
                    <div className="font-semibold text-black">{prettyAction(r.action)}</div>
                    <div className="text-[12px] text-neutral-500">{r.module}</div>
                </div>
            ),
        },
        { key: "user", label: "User", render: (r) => r.user },
        { key: "role", label: "Role", render: (r) => r.role },
        { key: "target", label: "Target", render: (r) => r.target || "—" },
        { key: "ip", label: "IP", render: (r) => <span className="text-[12px] text-neutral-500">{r.ip}</span> },
        { key: "status", label: "Status", render: (r) => <StatusPill status={r.status} /> },
        {
            key: "timestamp",
            label: "When",
            render: (r) => <span className="text-[12px] text-neutral-500">{friendlyTime(r.timestamp)}</span>,
        },
    ];

    return (
        <div data-testid="admin-audit-page">
            <PageHeader
                title="Audit Logs"
                subtitle="Every important action on the platform, streamed from the audit collection."
            />

            <div className="glass rounded-3xl p-4 mb-5 grid grid-cols-1 sm:grid-cols-3 gap-3">
                <Input
                    placeholder="Filter by user…"
                    value={user}
                    onChange={(e) => setUser(e.target.value)}
                    data-testid="audit-filter-user"
                />
                <Select
                    value={module}
                    onChange={(e) => setModule(e.target.value)}
                    data-testid="audit-filter-module"
                >
                    <option value="">All modules</option>
                    {["auth", "companies", "users", "roles", "departments", "agents", "invites", "plans", "settings", "system"].map((m) => (
                        <option key={m}>{m}</option>
                    ))}
                </Select>
                <Input
                    placeholder="Filter by action (e.g. user_created)…"
                    value={action}
                    onChange={(e) => setAction(e.target.value)}
                    data-testid="audit-filter-action"
                />
            </div>

            <DataTable
                cols={cols}
                rows={data || []}
                rowKey={(r) => r.id}
                loading={loading}
                empty={{ title: "No audit events match", hint: "Adjust the filters above." }}
            />
        </div>
    );
}

function prettyAction(a) {
    if (!a) return "Action";
    return a.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}
